import React, { useContext, useMemo, useState } from "react";
import { cn } from "utils";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { ExternalLink as ExternalLinkIcon } from "lucide-react";

import { JobsContext, AuthContext, AlertContext } from "context/providers";
import {
  formatJobStatus,
  generateJobExternalLink,
  getJobOrganization,
} from "utils/helpers/jobOppDetails";
import {
  getBullhornPublishedRates,
  isBullhornPublishedSalary,
} from "utils/common";

import {
  JOB_OPPORTUNITY_STATUSES,
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
  CURRENCY_CONVERSION_CONFIG,
} from "lookup";

import { DMPBadge, ExistingCustomerBadge } from "./Badges";
import HelpText from "./HelpText";

import SvgIcon from "./base/SvgIcon";
import { TABS } from "context/constants/common";
import {
  baseToTargetCurrency,
  loggedUserShouldINRConvert,
} from "utils/helpers/users";
import { useQuery } from "@apollo/client";
import {
  GET_JOB_OPPORTUNITY_MATCH_STATS,
  GET_JOB_WEB_APPLY_STATS,
} from "api/graphql/jobOpportunity";

const hasRateValue = (rate) =>
  rate?.value !== undefined && rate?.value !== null;

const getCustomerCompensationDisplay = (job) => {
  if (hasRateValue(job.minRate) || hasRateValue(job.maxRate)) {
    return {
      label: "Hourly Rate",
      min: job.minRate?.value,
      max: job.maxRate?.value,
    };
  }

  if (hasRateValue(job.minSalary) || hasRateValue(job.maxSalary)) {
    return {
      label: "Yearly Salary",
      min: job.minSalary?.value,
      max: job.maxSalary?.value,
    };
  }

  return null;
};

const getTalentCompensationDisplay = (job) => {
  if (hasRateValue(job.minTalentRate) || hasRateValue(job.maxTalentRate)) {
    return {
      label: "Talent Hourly Rate",
      min: job.minTalentRate?.value,
      max: job.maxTalentRate?.value,
    };
  }

  if (hasRateValue(job.minTalentSalary) || hasRateValue(job.maxTalentSalary)) {
    return {
      label: "Talent Yearly Salary",
      min: job.minTalentSalary?.value,
      max: job.maxTalentSalary?.value,
    };
  }

  const bullhornRates = getBullhornPublishedRates(job.metadata);

  if (hasRateValue(bullhornRates.low) || hasRateValue(bullhornRates.high)) {
    return {
      label: isBullhornPublishedSalary(bullhornRates.high)
        ? "Talent Yearly Salary (BH)"
        : "Talent Rate (BH)",
      min: bullhornRates.low?.value,
      max: bullhornRates.high?.value,
    };
  }

  return null;
};

const CompensationDisplay = ({ compensation, loggedInUser }) => {
  if (!compensation) {
    return null;
  }

  const min = compensation.min ?? compensation.max;
  const max = compensation.max;
  const hasRange = compensation.min != null && max != null;

  return (
    <span>
      {compensation.label}: ${min}
      {hasRange && <>&ndash;${max}</>}
      {loggedUserShouldINRConvert(loggedInUser) && (
        <span className="text-xs">
          {" "}
          (
          {baseToTargetCurrency(min, CURRENCY_CONVERSION_CONFIG.exchangeFactor)}
          {hasRange && (
            <>
              &ndash;
              {baseToTargetCurrency(
                max,
                CURRENCY_CONVERSION_CONFIG.exchangeFactor
              )}
            </>
          )}{" "}
          INR)
        </span>
      )}
    </span>
  );
};

export default function JobListingCard({ hit, legacyJob }) {
  const job = hit || legacyJob;

  const { data, error } = useQuery(GET_JOB_OPPORTUNITY_MATCH_STATS, {
    variables: {
      jobId: job?.id,
    },
    fetchPolicy: "cache-and-network",
    skip: !job,
  });

  const { user: loggedInUser } = useContext(AuthContext);

  const { data: webApplyData } = useQuery(GET_JOB_WEB_APPLY_STATS, {
    variables: {
      jobOpportunityId: job?.id,
      limit: 1000,
    },
    fetchPolicy: "cache-and-network",
    skip: !job?.id || !loggedInUser?.canAccessWebApplys,
  });

  const { updateJob } = useContext(JobsContext);
  const { addGraphQLAlert } = useContext(AlertContext);

  const [isUpdating, setIsUpdating] = useState(false);
  const [torcOwnerUsername, setTorcOwnerUsername] = useState(
    job.torcOwner?.username
  );

  const matches = useMemo(() => {
    const totals = {
      [JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]: 0,
      [JOB_APPLICATION_MATCH_STATUS.INTERESTED]: 0,
      [JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK]: 0,
      [JOB_APPLICATION_MATCH_STATUS.MATCHED]: 0,
      [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYCUSTOMER]: 0,
      [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER]: 0,
      [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]: 0,
      [JOB_APPLICATION_MATCH_STATUS.APPLIED]: 0,
      [JOB_APPLICATION_MATCH_STATUS.PRESHORTLISTED]: 0,
      [JOB_APPLICATION_MATCH_STATUS.TKSHORTLISTED]: 0,
      [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: 0,
      [JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]: 0,
    };

    if (error) {
      return totals;
    }

    data?.getJobOpportunity?.matches?.items.forEach((item) => {
      totals[item.status]++;
      if (item.subStatus) {
        totals[item.subStatus]++;
      }
    });

    return totals;
  }, [data?.getJobOpportunity?.matches?.items, error]);

  const webApplyCountDisplay = useMemo(() => {
    const items = webApplyData?.listJobLongList?.items ?? [];
    const nextToken = webApplyData?.listJobLongList?.nextToken;
    const n = items.length;

    return nextToken ? `${n}+` : n;
  }, [webApplyData?.listJobLongList]);

  const preCandidatesCount =
    matches[JOB_APPLICATION_MATCH_STATUS.PRESHORTLISTED] +
    matches[JOB_APPLICATION_MATCH_STATUS.TKSHORTLISTED];

  const jobDuration = (job) => {
    if (job?.jobLengthInWeeks) {
      return `${job.jobLengthInWeeks} weeks`;
    }

    if (job.jobLength) {
      return `${job?.jobLength} months`;
    }

    return "";
  };

  const statusColor = (() => {
    switch (job?.status) {
      case JOB_OPPORTUNITY_STATUSES.ACTIVE:
        return `#5523a2`;
      case JOB_OPPORTUNITY_STATUSES.PENDINGAPPROVAL:
        return `#e7b73c`;
      default:
        return `black`;
    }
  })();
  const textColor =
    job?.status === JOB_OPPORTUNITY_STATUSES.PENDINGAPPROVAL
      ? "text-black"
      : "text-white";

  const LinkToTab = ({ label, tab }) => (
    <Link
      to={`/jobOpps/${job.id}/details?tab=${tab}`}
      className="hover:text-sky-500 text-center leading-snug"
    >
      <span>{label}</span>
    </Link>
  );

  const updateJobTorcOwner = async () => {
    if (isUpdating) {
      return;
    }
    try {
      setIsUpdating(true);
      const payload = { id: loggedInUser.id, username: loggedInUser.username };
      await updateJob(job.id, { torcOwner: { ...payload } });

      setTorcOwnerUsername(payload.username);
    } catch (err) {
      addGraphQLAlert(err);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!job) {
    return null;
  }

  const customerCompensation = getCustomerCompensationDisplay(job);
  const talentCompensation = getTalentCompensationDisplay(job);

  return (
    <div
      data-testid="job-listing-card"
      aria-label="job card"
      className={cn(
        "w-full min-w-0 p-0.5 flex shadow-lg relative font-rubik rounded-lg",
        { "animate-pulse": isUpdating }
      )}
    >
      <div className="bg-white w-full min-w-0">
        <div className="sm:flex">
          <div
            className="relative -top-0.5 -left-0.5 w-40 flex items-center justify-center max-h-14 h-14 rounded-tl-lg"
            style={{ background: statusColor }}
          >
            <span className={cn("uppercase font-bold", textColor)}>
              {formatJobStatus(job.status)}
            </span>
          </div>

          <div className="ml-6 sm:ml-3 sm:mt-1 mt-8 sm:self-center">
            <div className="font-medium text-grey-800 text-sm">
              <p>
                <span title="Customer company name">
                  {getJobOrganization(job)} /{" "}
                </span>
                <span title="Customer username">
                  {job.customer?.username} /{" "}
                </span>
                <span title="Customer name">
                  {job.customer?.given_name} {job.customer?.family_name}
                </span>
              </p>
            </div>

            <Link to={`/jobOpps/${job.id}/details`}>
              <p className="font-bold text-grey-900 text-xl">{job?.title}</p>
            </Link>
            {job.isDMP && (
              <div className="flex items-center gap-x-2 my-2">
                <DMPBadge />
                {job.customer?.sfdcCustomerStatus === "EXISTING_CUSTOMER" && (
                  <ExistingCustomerBadge />
                )}
              </div>
            )}

            <div className="text-sky-500 italic font-bold text-sm">
              <p>
                <span>{job.jobType?.title || ""} </span>
                <span>{" / "}</span>
                <span>{jobDuration(job)}</span>
                {customerCompensation && (
                  <>
                    <span>{" / "}</span>
                    <CompensationDisplay
                      compensation={customerCompensation}
                      loggedInUser={loggedInUser}
                    />
                  </>
                )}
              </p>
              {talentCompensation && (
                <p>
                  <CompensationDisplay
                    compensation={talentCompensation}
                    loggedInUser={loggedInUser}
                  />
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="absolute top-2 right-4">
          <Link
            to={`/jobOpps/${job.id}/details${
              matches[JOB_APPLICATION_MATCH_STATUS.MATCHED] +
                matches[JOB_APPLICATION_MATCH_STATUS.APPLIED] >
              0
                ? `?tab=${TABS.MATCHED}`
                : ""
            }`}
          >
            <SvgIcon
              type="eyeVisible"
              className="w-[20px] hover:!fill-sky-500"
            />
          </Link>
        </div>

        <div className="w-full max-w-full min-w-0 pt-4 pb-6 px-4 sm:px-6 md:px-8 grid grid-cols-2 sm:grid-cols-4 gap-x-2 sm:gap-x-3 gap-y-5 justify-items-stretch">
          {loggedInUser?.canAccessPreCandidates && (
            <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
              <div className="flex items-center justify-center flex-wrap gap-x-1.5">
                <LinkToTab label="Pre-candidates" tab={TABS.PRECANDIDATES} />

                <HelpText text="Talent in pre-shortlisted or TK short-listed status (same list as the Pre-Candidates tab)." />
              </div>
              <span className="font-bold text-lg text-grey-900">
                {preCandidatesCount}
              </span>
            </div>
          )}
          {loggedInUser?.canAccessWebApplys && (
            <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
              <div className="flex items-center justify-center flex-wrap gap-x-1.5">
                <LinkToTab label="Web Applies" tab={TABS.LONGLISTED} />

                <HelpText text="Applications submitted via the web app that are not yet converted to a matcher-managed match (same list as the Web Applies tab)." />
              </div>
              <span className="font-bold text-lg text-grey-900">
                {webApplyCountDisplay}
              </span>
            </div>
          )}
          <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
            <div className="flex items-center justify-center flex-wrap gap-x-1.5">
              <LinkToTab label="Shortlisted" tab={TABS.SHORTLISTED} />

              <HelpText text="Profiles which are shortlisted but no emails/contact made with the talent" />
            </div>
            <span className="font-bold text-lg text-grey-900">
              {matches[JOB_APPLICATION_MATCH_STATUS.SHORTLISTED] +
                matches[JOB_APPLICATION_MATCH_STATUS.INTERESTED] +
                matches[JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK]}
            </span>
          </div>
          <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
            <div className="flex items-center justify-center flex-wrap gap-x-1.5">
              <LinkToTab label="Matched" tab={TABS.MATCHED} />

              <HelpText text="Selected profiles on which email has been sent to apply for the job. Haven't applied yet" />
            </div>
            <span className="font-bold text-lg text-grey-900">
              {matches[JOB_APPLICATION_MATCH_STATUS.MATCHED]}
            </span>
          </div>
          <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
            <div className="flex items-center justify-center flex-wrap gap-x-1.5">
              <LinkToTab label="Finalists" tab={TABS.MATCHED} />

              <HelpText text="Profiles selected as finalists to be presented to the customer" />
            </div>
            <span className="font-bold text-lg text-grey-900">
              {matches[JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]}
            </span>
          </div>
          <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
            <div className="flex items-center justify-center flex-wrap gap-x-1.5">
              <LinkToTab label="Rejected" tab={TABS.MATCHED} />

              <HelpText text="Profiles rejected by customer" />
            </div>
            <span className="font-bold text-lg text-grey-900">
              {matches[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYCUSTOMER] +
                matches[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER] +
                matches[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]}
            </span>
          </div>
          <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
            <div className="flex items-center justify-center flex-wrap gap-x-1.5">
              <LinkToTab label="Applied" tab={TABS.MATCHED} />

              <HelpText text="Talent currently applied for the job" />
            </div>
            <span className="font-bold text-lg text-grey-900">
              {matches[JOB_APPLICATION_MATCH_STATUS.APPLIED]}
            </span>
          </div>
          <div className="text-sm flex flex-col items-center text-grey-800 w-full min-w-0">
            <div className="flex items-center justify-center flex-wrap gap-x-1.5">
              <LinkToTab label="Skipped" tab={TABS.SKIPPED} />

              <HelpText text="Skipped profiles" />
            </div>
            <span className="font-bold text-lg text-grey-900">
              {matches[JOB_APPLICATION_MATCH_STATUS.SKIPPED]}
            </span>
          </div>
        </div>
        <div className="flex justify-between w-full px-4 pb-2">
          <div className="flex flex-col">
            <p className="text-xs">
              <span className="font-bold">Matcher: </span>
              {torcOwnerUsername || (
                <span
                  className={cn("text-sky-500 font-bold text-sm", {
                    "hover:underline cursor-pointer": !isUpdating,
                  })}
                  onClick={() => updateJobTorcOwner()}
                  disabled={isUpdating}
                  title="Claim Job as primary matcher"
                >
                  Claim
                </span>
              )}
            </p>
            <ExternalJobLink
              sfdcExternalID={job.sfdcExternalID}
              externalJobId={job.externalJobId}
            />
            <p className="text-xs">
              <span className="font-bold">Created Date: </span>
              {`${dayjs(job.createdAt).format("MM/DD/YYYY")}`}
            </p>
          </div>
          <div className="flex flex-col">
            {job.priorityLevel && (
              <div className="flex h-max items-center justify-end">
                <p className="text-xs ">
                  <span className="font-bold">Priority: </span>
                  {job.priorityLevel}
                </p>
              </div>
            )}
            {job.workMode && (
              <div className="flex h-max items-center justify-end text-xs gap-1">
                <span className="font-bold">Mode: </span>
                <p className="lowercase first-letter:uppercase">
                  {job.workMode}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ExternalJobLink = ({ externalJobId, sfdcExternalID }) => {
  if (!externalJobId || !sfdcExternalID) return null;

  const jobExternalLink = generateJobExternalLink(sfdcExternalID);

  if (!jobExternalLink) {
    return (
      <p className="text-xs">
        <span className="font-bold">rd-one Job Number: </span>
        {externalJobId}
      </p>
    );
  }

  return (
    <p className="text-xs flex gap-1">
      <span className="font-bold">rd-one Job Number: </span>
      <a
        href={jobExternalLink}
        target="_blank"
        rel="noreferrer"
        className="flex text-xs items-baseline gap-1 transition-all text-sky-500 opacity-90 hover:opacity-100 hover:underline"
      >
        {externalJobId}
        <ExternalLinkIcon size={12} strokeWidth={2.25} />
      </a>
    </p>
  );
};
